import {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {GUIDE_KEYS, RUTAS} from "constants/app";
import {GuideKey} from "core/constants/guideKeys";
import FormComponent from "ui/components/FormComponent";
import ResumeModal from "ui/components/ResumeModal";
import ConnectionErrorModal from "ui/components/ConnectionErrorModal";
import {useInitializeGuides} from "application/hooks/useInitializeGuides";

const FormPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const urlToken = searchParams.get("token");

    /*
    * Manejo de errores
    * */
    const [hasConnectionError, setHasConnectionError] = useState(false);
    const [formInstanceKey, setFormInstanceKey] = useState(`${Date.now()}`);

    /*
    * Validacion del token
    * */
    // 🟢 Validación basada en localStorage
    useEffect(() => {
        if (!urlToken) {
            navigate(RUTAS.ACCESO);
            return;
        }

        const lsToken = localStorage.getItem("user-token");
        const lsGuia = localStorage.getItem("guia"); // "GUIA_I" | "GUIA_II" | "GUIA_III"

        let lsCampaign = localStorage.getItem("campaign");
        if (!lsCampaign && urlToken) {
            try {
                const guardado = JSON.parse(localStorage.getItem("NOM035_tokenMap") || "{}");
                const campaignGuardada = guardado?.[urlToken]?.campaign;
                if (campaignGuardada) {
                    localStorage.setItem("campaign", campaignGuardada);
                    lsCampaign = campaignGuardada;
                }
            } catch {
                // tokenMap corrupto: se trata como si no hubiera campaña
            }
        }

        if (!lsToken || !lsCampaign || !lsGuia || lsToken !== urlToken) {
            navigate(RUTAS.ACCESODENEGADOS);
            return;
        }

        const GUIDE_PATH_MAP: Record<"GUIA_I" | "GUIA_II" | "GUIA_III", string> = {
            GUIA_I: "/guia-1",
            GUIA_II: "/guia-2",
            GUIA_III: "/guia-3",
        };
        const expectedPath = GUIDE_PATH_MAP[lsGuia as "GUIA_I" | "GUIA_II" | "GUIA_III"];

        // ✅ Permite SIEMPRE /guia-1 y además la ruta de la guía asignada
        const isCorrectGuidePath =
            pathname.includes("/guia-1") || pathname.includes(expectedPath);

        if (!isCorrectGuidePath) {
            navigate(RUTAS.ACCESO);
            return;
        }
    }, [urlToken, pathname, navigate]);

    /*
    * Validacion de la guia
    * */
    const [tokenRestaurado, setTokenRestaurado] = useState(false);
    const guiaLS = localStorage.getItem("guia");

    const allowedGuides: GuideKey[] = useMemo(() => {
        if (guiaLS === "GUIA_II") return [GUIDE_KEYS.I, GUIDE_KEYS.II];
        if (guiaLS === "GUIA_III") return [GUIDE_KEYS.I, GUIDE_KEYS.III];
        return [GUIDE_KEYS.I];
    }, [guiaLS]);

    const slug = location.pathname.split("/").pop();

    const slugToGuideKey = (slug?: string): GuideKey => {
        switch (slug) {
            case "guia-1":
                return GUIDE_KEYS.I;
            case "guia-2":
                return GUIDE_KEYS.II;
            case "guia-3":
                return GUIDE_KEYS.III;
            default:
                return GUIDE_KEYS.I;
        }
    };
    const currentGuideKey = useMemo(() => slugToGuideKey(slug), [slug]);


    useEffect(() => {
        if (!currentGuideKey || !tokenRestaurado) return;

        if (!allowedGuides.includes(currentGuideKey)) {
            navigate(`/${RUTAS.ACCESO}?token=${urlToken}`);
        }
    }, [allowedGuides, currentGuideKey, urlToken, navigate, tokenRestaurado]);

    const guideKey = guiaLS === "GUIA_II" ? GUIDE_KEYS.II
        : guiaLS === "GUIA_III" ? GUIDE_KEYS.III
            : GUIDE_KEYS.I;


    const {ready: readyGuides, error: errorGuides} = useInitializeGuides(urlToken ?? "", guideKey);

    useEffect(() => {
        if (errorGuides) {
            console.error("Error al cargar las guías:", errorGuides);
            setHasConnectionError(true);
        }
    }, [errorGuides]);

    /*
    * Restaurar respuestas del tokenMap
    *
    * */
    useEffect(() => {
        if (!urlToken) return;

        const tokenMapRaw = localStorage.getItem("NOM035_tokenMap");
        const tokenMap = tokenMapRaw ? JSON.parse(tokenMapRaw) : {};
        const tokenData = tokenMap[urlToken];

        if (!tokenData) {
            navigate(`${RUTAS.ACCESO}?token=${urlToken}`);
            return;
        }

        const {answers, scores} = tokenData;

        // Establecer user-token y bandera
        localStorage.setItem("user-token", urlToken);

        console.info("Token restaurado desde tokenMap");

        // Cargar answers/scores para la guía actual, directo desde tokenMap
        const guiaActual = currentGuideKey; // "GUIA_I" | "GUIA_II" | "GUIA_III"
        setSavedAnswers(answers?.[guiaActual] ?? {});
        setSavedScores(scores?.[guiaActual] ?? {});
        setTokenRestaurado(true);
    }, [urlToken, navigate, currentGuideKey]);


    /*
    * cargar preguntas
    * */
    const [savedAnswers, setSavedAnswers] = useState<Record<string, any>>({});
    const [savedScores, setSavedScores] = useState<Record<string, number>>({});

    const formOrder = useMemo(() => {
        const ordenCompleto = [GUIDE_KEYS.I, GUIDE_KEYS.II, GUIDE_KEYS.III];
        return ordenCompleto.filter(key => allowedGuides.includes(key));
    }, [allowedGuides]);

    const goToNextForm = () => {
        const idx = formOrder.indexOf(currentGuideKey);
        if (idx < formOrder.length - 1) {
            const next = formOrder[idx + 1];
            navigate(`/${getSlugFromGuideKey(next)}?token=${urlToken}`);
        } else {
            navigate(RUTAS.TERMINAR);
        }
    };

    const goToBeforeForm = () => {
        const idx = formOrder.indexOf(currentGuideKey);
        if (idx > 0) {
            const prev = formOrder[idx - 1];
            navigate(`/${getSlugFromGuideKey(prev)}?token=${urlToken}`);
        }
    };

    const getSlugFromGuideKey = (key: GuideKey) =>
        key === GUIDE_KEYS.I ? "guia-1" : key === GUIDE_KEYS.II ? "guia-2" : "guia-3";

    /*
    * Mostrar modal para continuar con el cuestionario
    * */
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [pendingFormKey, setPendingFormKey] = useState<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem("user-token");
        if (!token) return;

        const mapStr = localStorage.getItem("NOM035_tokenMap");
        const map = mapStr ? JSON.parse(mapStr) : {};
        const hasSavedAnswers = !!map[token]?.answers?.[currentGuideKey];

        if (hasSavedAnswers) {
            setPendingFormKey(currentGuideKey);
            setShowResumeModal(true);
        }
    }, [currentGuideKey]);

    const handleResumeDecision = (resume: boolean) => {
        if (!pendingFormKey) return;

        const token = localStorage.getItem("user-token");
        const mapStr = localStorage.getItem("NOM035_tokenMap");
        const map = mapStr ? JSON.parse(mapStr) : {};

        if (!resume) {
            // Reiniciar SOLO en tokenMap
            if (token && map[token]) {
                delete map[token].answers?.[pendingFormKey];
                delete map[token].scores?.[pendingFormKey];
                localStorage.setItem("NOM035_tokenMap", JSON.stringify(map));
            }
            localStorage.removeItem(`NOM035_${pendingFormKey}_section`);

            setSavedAnswers({});
            setSavedScores({});
            setFormInstanceKey(`${Date.now()}`);
        } else {
            // 🟢 Restaurar desde tokenMap
            const a = token && map[token]?.answers?.[pendingFormKey] ? map[token].answers[pendingFormKey] : {};
            const s = token && map[token]?.scores?.[pendingFormKey] ? map[token].scores[pendingFormKey] : {};
            setSavedAnswers(a);
            setSavedScores(s);
            setFormInstanceKey(`${Date.now()}`);
        }

        setShowResumeModal(false);
        setPendingFormKey(null);
    };

    /*
    * Reintentos
    * */
    const handleRetry = () => {
        if (urlToken) {
            window.location.reload();
        } else {
            navigate(RUTAS.ACCESO);
        }
    };

    const handleCancel = () => {
        navigate(RUTAS.ACCESO);
    };

    return (
        <>
            <FormComponent
                key={`form-${formInstanceKey}`}
                formKey={currentGuideKey}
                guiaKey="NOM035"
                initialAnswers={savedAnswers}
                initialScores={savedScores}
                onComplete={goToNextForm}
                goBack={goToBeforeForm}
                formOrder={formOrder}
            />
            <ResumeModal
                isOpen={showResumeModal}
                onResume={() => handleResumeDecision(true)}
                onReset={() => handleResumeDecision(false)}
                onClose={() => {
                    setShowResumeModal(false);
                    setPendingFormKey(null);
                }}
                formType={pendingFormKey ?? currentGuideKey}
            />
            <ConnectionErrorModal
                isOpen={hasConnectionError}
                onRetry={handleRetry}
                onCancel={handleCancel}
            />
        </>
    );
};

export default FormPage;